import React from "react";
import Modal from "../../../shared/ui/Modal";
import Pagination from "../../../shared/ui/Pagination";

import DevOnly from "../../../shared/ui/DevOnly";
import { useForm } from "react-hook-form";
import { useAuth } from "../../auth/AuthContext";
import { useItemForm } from "../model/ItemFormContext";
import { getMarketSiteName } from "../../../shared/lib/helpers";
import {
    searchUserShopItem,
    fetchUserShopItemImageUrl
} from "../../shops/shops.api";

interface Props {
    show: boolean;
    onHide: () => {};
}

const ShopItemSearchModal: React.FC<Props> = ({ show, onHide }: Props) => {
    const auth = useAuth();
    const { shop, setValue } = useItemForm();
    const [items, setItems] = React.useState<any[]>([]);
    const [page, setPage] = React.useState<number>(1);
    const [pages, setPages] = React.useState<number[]>([]);

    interface UserItemSearchFormInput {
        search_keyword?: string;
    }
    const [formErrorMessage, setFormErrorMessage] = React.useState<string | null>(null);
    const [formProcessingMessage, setFormProcessingMessage] = React.useState<string | null>(null);
    const {
        register,
        handleSubmit,
    } = useForm<UserItemSearchFormInput>({
        defaultValues: {
            search_keyword: "",
        },
    });

    const onSubmit = async (data: UserItemSearchFormInput) => {
        if (!shop) return;

        setFormErrorMessage(null); // Reset error state
        setFormProcessingMessage("처리 중입니다..."); // Set loading state

        try {
            const result = await searchUserShopItem({
                shop_id: shop.id,
                search_keyword: data.search_keyword || "",
                page: page,
                auth: auth,
            });
            setItems(result.items);
            setPages(result.pages);
            setPage(result.page);
        } catch (e: any) {
            setFormErrorMessage("상품 검색 중 오류가 발생했습니다.");
            console.error(e);
        } finally {
            setFormProcessingMessage(null); // Reset loading state
        }
    };

    const handleClickPage = async (_p: number) => {
        console.log(`handleClickPage()`);
        setFormErrorMessage(null); // Reset error state
        setFormProcessingMessage("처리 중입니다..."); // Set loading state

        try {
            const result = await searchUserShopItem({
                shop_id: shop.id,
                search_keyword: "",
                page: _p,
                auth: auth,
            });
            setItems(result.items);
            setPages(result.pages);
            setPage(result.page);
        } catch (e: any) {
            setFormErrorMessage("상품 검색 중 오류가 발생했습니다.");
            console.error(e);
        } finally {
            setFormProcessingMessage(null); // Reset loading state
        }
    };

    const handleSelectItem = async (item: any) => {
        if (!shop) return;

        setFormErrorMessage(null);
        setFormProcessingMessage("처리 중입니다...");

        try {
            setValue("shop_item_id", item.id);
            setValue("shop_item_gid", item.gid);
            setValue("name", item.name);
            setValue("price", item.price);
            setValue("discount", item.discount);
            setValue("shipping_fee", item.delivery_fee);
            setValue("priced_at", item.priced_at);

            // shop_item_image_url 필드 처리
            // ----
            // 1. smartstore: 그냥 저장
            // 2. gmarket: 그냥 저장
            // 3. eleventh: 그냥 저장
            // 4. auction: image_url에 `auction`이 포함되어 있지 않은 경우
            //    playwrightapp의 /crawl/auctionitemimageurl/:auctionId/:itemId 호출
            //    단 playwrightapp은 외부 공개가 되면 안되므로 이와 연결된 djangoapp의 route를 호출
            if (shop.market_site === "auction" && item.image_url && !item.image_url.includes("auction")) {
                try {
                    const response = await fetchUserShopItemImageUrl({
                        auth: auth,
                        shop_id: shop.id,
                        shop_item_id: item.id,
                    });
                    console.log("Fetched image URL:", response);
                    setValue("shop_item_image_url", response.imageUrl || "");
                } catch (e: any) {
                    setFormErrorMessage("상품 이미지 조회 중 오류가 발생했습니다.");
                    console.error(e);
                }
            } else {
                setValue("shop_item_image_url", item.image_url || "");
            }

            setItems([]);
            setPage(1);
            setPages([]);
            onHide();
        } catch (e: any) {
            setFormErrorMessage("상품 상세 조회 중 오류가 발생했습니다.");
            console.error(e);
        } finally {
            setFormProcessingMessage(null);
        }
    };

    return (
        shop && (
            <Modal
                show={show}
                onHide={() => {
                    setItems([]);
                    setPages([]);
                    setPage(1);
                    onHide();
                }}
                backdrop="static"
                size="xl"
                scrollable={true}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {shop.market_site && (
                            <span>
                                {getMarketSiteName(shop.market_site)}
                                상품 검색
                                {/*<small>{shop.name} {shop.api_key} {shop.api_secret}</small>*/}
                            </span>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form className="mb-3">
                        <div className="d-flex justify-content-between">
                            {shop.market_site == 'smartstore' && (
                                <>
                                    <div className="col">
                                        <input
                                            type="text"
                                            id="id_search_keyword"
                                            className={`form-control`}
                                            placeholder="상품ID를 입력하세요."
                                            {...register("search_keyword", { required: false })}
                                        />
                                    </div>
                                </>
                            )}
                            {shop.market_site == 'auction' && (
                                <>
                                    <div className="col">
                                        <input
                                            type="text"
                                            id="id_search_keyword"
                                            className={`form-control`}
                                            placeholder="상품 ID를 입력하세요."
                                            {...register("search_keyword", { required: false })}
                                        />
                                    </div>
                                </>
                            )}
                            {shop.market_site == 'gmarket' && (
                                <>
                                    <div className="col">
                                        <input
                                            type="text"
                                            id="id_search_keyword"
                                            className={`form-control`}
                                            placeholder="상품 ID를 입력하세요."
                                            {...register("search_keyword", { required: false })}
                                        />
                                    </div>
                                </>
                            )}
                            {(shop.market_site == 'eleventh' || shop.market_site == 'lotteon' || shop.market_site == 'ssgdotcom') && (
                                <>
                                    <div className="col">
                                        <input
                                            type="text"
                                            id="id_search_keyword"
                                            className={`form-control`}
                                            placeholder="상품 ID를 입력하세요."
                                            {...register("search_keyword", { required: false })}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="col-auto ms-2">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    검색
                                </button>
                            </div>
                        </div>
                    </form>

                    {formErrorMessage && (
                        <div className="alert alert-danger" role="alert">
                            {formErrorMessage}
                        </div>
                    )}
                    {formProcessingMessage && (
                        <div className="alert alert-info" role="alert">
                            {formProcessingMessage}
                        </div>
                    )}

                    <table className="table shop-item-search-table">
                        <thead>
                            <tr>
                                <th>상품ID</th>
                                <th>상품명</th>
                                <th>판매가</th>
                                <th style={{ minWidth: 80 }}>할인액</th>
                                <th style={{ minWidth: 80 }}>배송비</th>
                                <th className="text-center" style={{ minWidth: 80 }}>
                                    선택
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length < 1 && (
                                <tr>
                                    <td colSpan={100} align="center" className="py-3">
                                        검색된 상품이 없습니다
                                    </td>
                                </tr>
                            )}
                            {items.length > 0 &&
                                items.map((item, index) => (
                                    <tr key={`"${index}"`}>
                                        <td>
                                            <div>{item.id}</div>
                                            <DevOnly>{item.gid}</DevOnly>
                                        </td>
                                        <td>{item.name}</td>
                                        <td className="text-end">
                                            {item.price ? item.price.toLocaleString() : 0}
                                        </td>
                                        <td className="text-end">
                                            {item.discount ? item.discount.toLocaleString() : 0}
                                        </td>
                                        <td className="text-end">
                                            {item.shipping_fee
                                                ? item.shipping_fee.toLocaleString()
                                                : 0}
                                        </td>
                                        <td className="text-center">
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={() => handleSelectItem(item)}
                                            >
                                                선택
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>

                    {items.length > 0 && (
                        <div className="d-flex justify-content-center">
                            <Pagination>
                                {pages.map((_p) => {
                                    return (
                                        <Pagination.Item
                                            key={_p}
                                            active={_p == page}
                                            onClick={() => handleClickPage(_p)}
                                        >
                                            {_p}
                                        </Pagination.Item>
                                    );
                                })}
                            </Pagination>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                            setItems([]);
                            setPage(1);
                            setPages([]);
                            onHide();
                        }}
                    >
                        닫기
                    </button>
                </Modal.Footer>
            </Modal>
        )
    );
};

export default ShopItemSearchModal;
